import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { evaluationCriteriaApi } from "../../../api/evaluationCriteria";
import { notifications } from "@mantine/notifications";

export const useEvaluationCriteria = (tenderId: string) => {
  return useQuery({
    queryKey: ['evaluationCriteria', tenderId],
    queryFn: async () => {
      const res = await evaluationCriteriaApi.listCriteria(tenderId);
      return res.data;
    },
    enabled: !!tenderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateEvaluationCriteria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: any }) =>
      evaluationCriteriaApi.createCriteria(tenderId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationCriteria', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Evaluation criteria created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create evaluation criteria",
        color: "red",
      });
    },
  });
};

export const useUpdateEvaluationCriteria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, criteriaId, data }: { tenderId: string; criteriaId: string; data: any }) =>
      evaluationCriteriaApi.updateCriteria(tenderId, criteriaId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationCriteria', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Evaluation criteria updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update evaluation criteria",
        color: "red",
      });
    },
  });
};

export const useRemoveEvaluationCriteria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, criteriaId }: { tenderId: string; criteriaId: string }) =>
      evaluationCriteriaApi.removeCriteria(tenderId, criteriaId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationCriteria', variables.tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', variables.tenderId] });
      notifications.show({
        title: "Success",
        message: "Evaluation criteria removed successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to remove evaluation criteria",
        color: "red",
      });
    },
  });
};

export const useCreateDefaultEvaluationCriteria = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenderId: string) => evaluationCriteriaApi.createDefaultCriteria(tenderId),
    onSuccess: (_data, tenderId) => {
      queryClient.invalidateQueries({ queryKey: ['evaluationCriteria', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      notifications.show({
        title: "Success",
        message: "Default evaluation criteria created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create default evaluation criteria",
        color: "red",
      });
    },
  });
};
